import formatMoney from "./utils/format-money";

const Minicart = {
	/** Maintains the current state of the cart, this is automatically updated when a page loads. */
	state: {
		drawer: null,
		subtotalDisplay: null,
		items: null,
		itemString: null,
		drawerOpen: false,
		errorObject: {},
	},

	/**
	 * Submits an add to cart form
	 *    Stops http submit
	 *    Creates itemData object for current form
	 *    Sends the itemData object to the Minicart.addItemToCart function
	 * @param {Element} form - The form element that is being submitted
	 */
	submitAtcForm: function (form) {
		event.preventDefault();
		let itemData = {
			id: 0,
			quantity: 1,
			properties: {},
		};
		for (let element of form.elements) {
			if (element.name.includes("properties")) {
				itemData.properties[element.name.split("[").pop().split("]")[0]] =
					element.value;
			} else if (element.name in itemData) {
				itemData[element.name] = element.value;
			}
		}
		this.addItemToCart(itemData);
	},

	/**
	 * Posts an itemData object to the cart
	 *    THEN Gets the current cart and updates the Minicart.state
	 *         Populates the cart drawer
	 * @param {Object} itemData - The object representation of the item you want to add to the cart
	 */
	addItemToCart: async function (itemData = {}) {
		await fetch("/cart/add.js", {
			method: "POST",
			body: JSON.stringify(itemData),
			headers: {
				"Content-Type": "application/json; charset=utf-8",
			},
		});
		await fetch("/cart.js")
			.then((cartResponse) => cartResponse.json())
			.then((jsonResponse) => {
				let itemString = "";
				for (let item of jsonResponse.items) {
					itemString += `${item.variant_id}:${item.quantity},`;
				}
				itemString = itemString.substring(0, itemString.length - 1);
				this.state.itemString = itemString;
				this.state.items = jsonResponse.items;
				this.populateCartDrawer(true);
			});
	},

	/**
	 * Populates the cart drawer with the current cart items display
	 *    Optionally: Displays the cart drawer when finished
	 * @param {Boolean} openDrawer - Whether or not to open the drawer when it has been populated. Default: false (do not open)
	 */
	populateCartDrawer: async function (openDrawer = false) {
		const itemContainer = this.state.drawer.querySelector(
			"#MinicartItemContainer"
		);
		let freeShippingBar = document.querySelector("#MinicartFreeShipping");
		itemContainer.innerHTML = "";

		await fetch("/cart?view=cartitems")
			.then((cartResponse) => cartResponse.text())
			.then((htmlResponse) => {
				itemContainer.innerHTML = htmlResponse;
				// check to see if anything wasnt added last render
				if (this.state.errorObject.message) {
					const error = this.state.errorObject;
					// find the error box and display it
					const cartItemErrorBox = document.getElementsByClassName(
						`variant-${error.variant}`
					)[0];
					cartItemErrorBox.classList.remove("d-none");
					cartItemErrorBox.innerText = error.message;
					// reset state
					this.state.errorObject = {};
				}
			});
		await fetch("/cart?view=freeshipping")
			.then((cartResponse) => cartResponse.text())
			.then((htmlResponse) => {
				if (freeShippingBar) {
					freeShippingBar.innerHTML = htmlResponse;
				}
			});
		if (openDrawer) {
			this.toggleDrawer("open");
		}
		/* Add cart count to title */
		const cartIcon = document.querySelector("[data-minicartcount]");
		await fetch("/cart.js")
			.then((cartResponse) => cartResponse.json())
			.then((data) => {
				cartIcon.innerHTML = data.item_count;
				// Added to make sure the cart subtotal is updated every time the cart opens //
				this.state.subtotalDisplay.innerText = formatMoney(data.total_price);
			});
	},

	/**
	 * Sets the quantity of any single lineitem in the cart.
	 *    Creates the item data object and converts all keys and values to strings
	 *    Posts the item data object to /cart/change.js
	 *        THEN Receives the new state of the cart
	 *             Updates the subtotal display to reflect the new state of the cart
	 *             Populates the cart drawer
	 * @param {String, Integer} variantId
	 * @param {String, Integer} newQuantity
	 */
	setLineitemQuantity: async function (variantId, newQuantity) {
		let itemData = {
			id: variantId.toString(),
			quantity: newQuantity.toString(),
		};
		await fetch("/cart/change.js", {
			method: "POST",
			body: JSON.stringify(itemData),
			headers: {
				"Content-Type": "application/json; charset=utf-8",
			},
		})
			.then((cartResponse) => cartResponse.json())
			.then((jsonResponse) => {
				const subTotal = formatMoney(jsonResponse.total_price);

				// check to see if sub total of cart has changed
				if (this.state.subtotalDisplay.innerText === subTotal) {
					// if it hasn't it means the item hasn't been added to cart
					// let's update the state error object with the id of the offending item
					this.state.errorObject = {
						message: "Sorry you can't add this item to your bag!",
						variant: variantId,
					};
				}
				this.state.subtotalDisplay.innerText = subTotal;
				this.populateCartDrawer();
			});
	},

	/**
	 * Toggles the current display state of the drawer
	 * @param {String} state - One of 'open', 'close', 'toggle'. Default: toggle
	 */
	toggleDrawer: function (state = "toggle") {
		let drawer = this.state.drawer;
		let drawerOpen = this.state.drawerOpen;
		switch (state) {
			case "open":
				drawer.classList.add("open");
				document.body.style.overflowY = "hidden";
				this.state.drawerOpen = true;
				break;
			case "close":
				drawer.classList.remove("open");
				document.body.style.overflowY = "auto";
				this.state.drawerOpen = false;
				break;
			default:
				drawerOpen
					? drawer.classList.remove("open")
					: drawer.classList.add("open");
				drawerOpen
					? (document.body.style.overflowY = "auto")
					: (document.body.style.overflowY = "hidden");
				this.state.drawerOpen = !drawerOpen;
		}
	},

	/**
	 * Initializes the minicart
	 *    Fetches the current cart
	 *    THEN Sets the state:items to the array of item objects in the cart
	 *         Sets the state:itemString to the variant_id:quantity string for the cart
	 *         Populates the cart drawer
	 *    Grabs add to cart forms on the page and directs them to the addToCart function
	 *    Sets the state:drawer to the #MinicartDrawer element
	 *    Sets the state:subtotalDisplay to the #MinicartSubtotal element
	 *    Adds an event listener to close the cart drawer if click is outsite it
	 */
	initialize: async function () {
		// Set state
		let itemString = "";
		fetch("/cart.js")
			.then((cartResponse) => cartResponse.json())
			.then((jsonResponse) => {
				for (let item of jsonResponse.items) {
					itemString += `${item.variant_id}:${item.quantity},`;
				}
				itemString = itemString.substring(0, itemString.length - 1);
				this.state.itemString = itemString;
				this.state.items = jsonResponse.items;
				this.populateCartDrawer();
			});

		// Sets remaining Minicart.state properties
		this.state.drawer = document.querySelector("#MinicartDrawer");
		this.state.subtotalDisplay = document.querySelector("#MinicartSubtotal");

		// Add event to close when clicking outside minicart drawer
		this.state.drawer.addEventListener("click", function () {
			let minicartMain = this.querySelector(".minicart__main");
			if (minicartMain && !minicartMain.contains(event.target)) {
				Minicart.toggleDrawer("close");
			}
		});

		// Direct ATC forms
		const addToCartForms = Array.from(
			document.querySelectorAll("form[action='/cart/add']")
		);
		for (let formIndex in addToCartForms) {
			let thisForm = addToCartForms[formIndex];
			thisForm.addEventListener("submit", function () {
				Minicart.submitAtcForm(thisForm);
			});
		}
	},
};

window.Minicart = Minicart;
document.addEventListener("DOMContentLoaded", function () {
	Minicart.initialize();
});

export default Minicart;
